import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ClipboardList, Search, Pencil, Trash2, RefreshCcw } from "lucide-react";
import { Modal, FieldSelect, FieldTextarea, Field } from "./AdminBusinesses";

const RESULTS = ["", "interested", "not_interested", "maybe_later", "wants_follow_up",
                  "already_signed_up", "no_decision_maker", "need_to_return", "wrong_business"];
const FOLLOWUP = ["", "none", "pending", "in_progress", "followed_up", "converted", "dead_lead"];

export default function AdminOutreachLogs({ followUpOnly = false }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (followUpOnly) params.set("follow_up_only", "true");
    const { data } = await api.get(`/admin/outreach-logs?${params}`);
    setItems(data.items || []);
  }, [q, status, followUpOnly]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (patch) => {
    try {
      await api.put(`/admin/outreach-logs/${editing.id}`, patch);
      toast.success("Log updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const doDelete = async () => {
    await api.delete(`/admin/outreach-logs/${confirm.id}`);
    toast.success("Log deleted.");
    setConfirm(null);
    load();
  };

  return (
    <div data-testid={followUpOnly ? "admin-follow-up-page" : "admin-outreach-logs-page"}>
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <ClipboardList className="w-5 h-5" /> {followUpOnly ? "Lead Follow-Up" : "Outreach Logs"}
      </h1>
      <p className="text-sm text-slate-600 mt-1">
        {followUpOnly
          ? "Leads that promoters flagged as wants-follow-up, maybe-later, or need-to-return."
          : "Every business visit log submitted by your field promoters."}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Business / city / province..."
                 className="pl-7 pr-3 py-1.5 text-sm rounded-lg border border-slate-300" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg border border-slate-300">
          {RESULTS.map((s) => <option key={s} value={s}>{s || "Any result"}</option>)}
        </select>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">{items.length} logs</span>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Business</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">City / Province</th>
              <th className="text-left p-2">Promoter</th>
              <th className="text-left p-2">Result</th>
              <th className="text-left p-2">Follow-up</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-t border-slate-100">
                <td className="p-2 text-slate-600">{(l.contacted_date || l.created_at || "").slice(0, 10)}</td>
                <td className="p-2 font-medium">{l.business_name}</td>
                <td className="p-2 text-slate-600">{l.business_type}</td>
                <td className="p-2 text-slate-600">{[l.city, l.province].filter(Boolean).join(", ") || "-"}</td>
                <td className="p-2 text-slate-600">{l.promoter_name}</td>
                <td className="p-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">{l.result_status}</span></td>
                <td className="p-2 text-xs">{l.follow_up_status || "none"}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button data-testid={`log-edit-${l.id}`} onClick={() => setEditing(l)}
                          className="text-xs px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> View / Edit
                  </button>
                  <button data-testid={`log-delete-${l.id}`} onClick={() => setConfirm(l)}
                          className="ml-1 text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No logs found.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <LogEditModal item={editing} onClose={() => setEditing(null)} onSave={onSave} />}
      {confirm && (
        <Modal title={`Delete log for "${confirm.business_name}"?`} onClose={() => setConfirm(null)} testId="confirm-delete-log">
          <p className="text-sm text-slate-700">This permanently removes the outreach log. Use only for spam / invalid entries.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
            <button data-testid="confirm-delete-log-yes" onClick={doDelete} className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white">Yes, delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LogEditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    business_name: item.business_name || "",
    result_status: item.result_status || "",
    follow_up_status: item.follow_up_status || "none",
    admin_notes: item.admin_notes || "",
  });
  return (
    <Modal title={`Log: ${item.business_name}`} onClose={onClose} testId="log-edit-modal">
      <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto pr-1">
        <LabeledBlock label="Submitted by">{item.promoter_name}</LabeledBlock>
        <LabeledBlock label="Contact">{item.contact_name} {item.contact_position ? `(${item.contact_position})` : ""}</LabeledBlock>
        <LabeledBlock label="Address">{[item.business_address, item.city, item.province].filter(Boolean).join(", ") || "-"}</LabeledBlock>
        <LabeledBlock label="Contact channels">
          <ul className="text-xs text-slate-700 space-y-0.5">
            {item.business_phone && <li>📞 {item.business_phone}</li>}
            {item.business_email && <li>✉️ {item.business_email}</li>}
            {item.facebook_url && <li>FB: <a href={item.facebook_url} target="_blank" rel="noreferrer" className="underline text-emerald-700">{item.facebook_url}</a></li>}
            {item.whatsapp_or_viber && <li>WA/Viber: {item.whatsapp_or_viber}</li>}
            {!item.business_phone && !item.business_email && !item.facebook_url && !item.whatsapp_or_viber && <li>-</li>}
          </ul>
        </LabeledBlock>
        <LabeledBlock label="Promoter notes">{item.notes || "-"}</LabeledBlock>
        <LabeledBlock label="Promoter comments">{item.promoter_comments || "-"}</LabeledBlock>

        <Field label="Business name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
        <FieldSelect label="Result" value={form.result_status}
                       options={["interested", "not_interested", "maybe_later", "wants_follow_up",
                                  "already_signed_up", "no_decision_maker", "need_to_return", "wrong_business"]}
                       onChange={(v) => setForm({ ...form, result_status: v })} />
        <FieldSelect label="Follow-up status" value={form.follow_up_status}
                       options={["none", "pending", "in_progress", "followed_up", "converted", "dead_lead"]}
                       onChange={(v) => setForm({ ...form, follow_up_status: v })} />
        <FieldTextarea label="Admin internal notes (promoter cannot see this)"
                         value={form.admin_notes} onChange={(v) => setForm({ ...form, admin_notes: v })}
                         testId="log-admin-notes" />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Close</button>
        <button data-testid="log-edit-save" onClick={() => onSave(form)} className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white">Save</button>
      </div>
    </Modal>
  );
}

function LabeledBlock({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{children}</div>
    </div>
  );
}
